"use client";

import React, { useState } from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import { useBillingCompanyContext } from "../../providers";

export interface IPaymentReminderConfigOption {
  whatsapp_campaign_automation_configuration_uuid: string;
  configuration_name: string;
}

interface IPaymentReminderAutoSearchProps {
  value?: string;
  label?: string;
  disabled?: boolean;
  onSelect?: (item: IPaymentReminderConfigOption | null) => void;
  sx?: any;
}

/**
 * Autosearch for Payment Reminder configurations (created in the "Reminders" tab).
 * Backed by /campaign/get-whatsapp-campaign-automation-configurations — the same
 * endpoint used by ReminderConfigList — so options always reflect actual configured reminders.
 */
export const PaymentReminderAutoSearch: React.FC<IPaymentReminderAutoSearchProps> = ({
  value = "",
  label,
  disabled,
  onSelect,
  sx,
}) => {
  const companyOutlet = useBillingCompanyContext();

  const hasFetchedRef = React.useRef(false);

  const [options, setOptions] = useState<IPaymentReminderConfigOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const company_outlet_uuid = companyOutlet.comapny.company_outlet_uuid;

  const fetchReminderConfigs = async (search: string) => {
    setLoading(true);
    try {
      let query = "";
      if (search) {
        query = `search=${encodeURIComponent(search)}&`;
      }
      const url = `/campaign/get-whatsapp-campaign-automation-configurations?${query}company_outlet_uuid=${company_outlet_uuid}`;
      const res = await api.get(url);
      setOptions(res.data.data || []);
    } catch (error) {
      console.error("Error fetching payment reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleFocus = () => {
    if (!hasFetchedRef.current) {
      fetchReminderConfigs("");
      hasFetchedRef.current = true;
    }
  };

  return (
    <>
      {label && <CustomFormLabel>{label}</CustomFormLabel>}

      <Autocomplete
        sx={{
          ...sx,
          ".MuiOutlinedInput-root": {
            paddingTop: "1.5px",
            paddingBottom: "1.5px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            width: "100%",
          },
        }}
        freeSolo
        fullWidth
        size="small"
        disabled={disabled}
        options={options}
        loading={loading}
        inputValue={inputValue}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.configuration_name || ""
        }
        isOptionEqualToValue={(option, val) =>
          option.whatsapp_campaign_automation_configuration_uuid ===
          val.whatsapp_campaign_automation_configuration_uuid
        }
        onFocus={handleFocus}
        onInputChange={(_event, newValue, reason) => {
          if (reason === "input") {
            setInputValue(newValue);
            fetchReminderConfigs(newValue);
          }
        }}
        onChange={(_event, newValue) => {
          if (typeof newValue === "object" && newValue) {
            onSelect?.(newValue);
          } else {
            onSelect?.(null);
          }
        }}
        noOptionsText="No reminders found"
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label="Payment Reminder"
            placeholder="Search reminder..."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};
